import { ShareLocation } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  RebuildRouteDropdownDefaultActionSystemDefaultReset,
  RebuildRouteDropdownDefaultRebuildHeading,
  RebuildRouteDropdownSkipCacheRow,
  RebuildRouteDropdownSubmitButton,
} from "@ryde/controls";
import {
  formatRebuildRouteSaveCacheButtonCopy,
  formatRebuildRouteSaveCacheButtonTooltip,
  IRebuildRouteDefaultActionOptions,
  RebuildRouteTypeEnum,
  resolveSkipCacheDefaultRebuildType,
  type DirectionsServiceEnum,
} from "@ryde/shared";

interface IStationRouteRebuildRouteDropdownDefaultActionProps {
  skipCache: boolean;
  isLoading?: boolean;
  lastUsedProvider?: DirectionsServiceEnum | null;
  systemDefaultProvider: DirectionsServiceEnum;
  onSkipCacheChange: (skipCache: boolean) => void;
  onRebuild: (
    rebuildType: RebuildRouteTypeEnum,
    options?: IRebuildRouteDefaultActionOptions,
  ) => void;
}

export const StationRouteRebuildRouteDropdownDefaultAction: FC<
  IStationRouteRebuildRouteDropdownDefaultActionProps
> = ({
  skipCache,
  isLoading,
  lastUsedProvider,
  systemDefaultProvider,
  onSkipCacheChange,
  onRebuild,
}) => {
  const { t } = useTranslation();

  const rebuildType = useMemo(
    () => resolveSkipCacheDefaultRebuildType({ skipCachedPaths: skipCache }),
    [skipCache],
  );

  const cacheTooltip = skipCache
    ? t("general.REBUILD_REFRESH_CACHE_TOOLTIP")
    : t("general.SKIP_CACHED_PATHS_TOOLTIP");

  const fullRebuildButtonCopy = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonCopy({
        lastUsedProvider,
        systemDefaultProvider,
        skipCachedPaths: skipCache,
        t,
      }),
    [lastUsedProvider, skipCache, systemDefaultProvider, t],
  );

  const saveCacheButtonTooltip = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonTooltip({
        skipCachedPaths: skipCache,
        t,
      }),
    [skipCache, t],
  );

  return (
    <>
      <RebuildRouteDropdownDefaultRebuildHeading />

      <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--default">
        <RebuildRouteDropdownSkipCacheRow
          checked={skipCache}
          disabled
          label={t("general.SKIP_CACHE")}
          tooltip={cacheTooltip}
          onChange={onSkipCacheChange}
        />

        <RebuildRouteDropdownSubmitButton
          label={fullRebuildButtonCopy.title}
          hint={fullRebuildButtonCopy.hint}
          tooltip={saveCacheButtonTooltip}
          startIcon={<ShareLocation />}
          isLoading={isLoading}
          isSecondary
          isSkipCacheActive={skipCache}
          onClick={() => onRebuild(rebuildType)}
        />

        <RebuildRouteDropdownDefaultActionSystemDefaultReset
          lastUsedProvider={lastUsedProvider}
          systemDefaultProvider={systemDefaultProvider}
          isLoading={isLoading}
          onResetToSystemDefault={() =>
            onRebuild(RebuildRouteTypeEnum.Default, {
              provider: systemDefaultProvider,
            })
          }
        />
      </div>
    </>
  );
};
